import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  ArrowLeft,
  User,
  Mail,
  Shield,
  Calendar,
  Key,
  Trash2,
  RotateCcw,
  Edit,
  UserCog,
  Clock,
  AlertTriangle,
  Users,
  Trophy,
  Activity,
  TrendingUp,
  Zap,
  Target,
  BarChart3,
  FlaskConical,
  UserCheck,
  ShieldCheck,
  Link2,
  Copy,
  Fingerprint,
} from 'lucide-react';

/**
 * Resolve the actual auth provenance of a user record by looking at which
 * identity anchor is populated. The previous logic was `google_id ? Google
 * : Email/Password`, which mis-labels every device-UUID and App Store
 * receipt user as "Email/Password" because they all carry a synthetic
 * `tm-xxxx@device.smarterhoops.app` email.
 *
 * Priority order matches the native bootstrap (App.js resolveAuth):
 *   1. App Store receipt (composite Apple-ID + originalPurchaseDate)
 *   2. Sign in with Apple (apple_subject_id from JWKS-verified token)
 *   3. Google OAuth
 *   4. Device UUID (Capacitor fallback)
 *   5. Email/Password (admin / legacy)
 */
function deriveAuthProvider(u) {
  if (!u) return { label: 'Unknown', tone: 'bg-slate-100 text-slate-700' };
  if (u.apple_app_apple_id || u.apple_app_transaction_id) {
    return { label: 'App Store Receipt', tone: 'bg-amber-100 text-amber-800' };
  }
  if (u.apple_subject_id) {
    return { label: 'Sign in with Apple', tone: 'bg-slate-900 text-white' };
  }
  if (u.google_subject_id || u.google_id) {
    return { label: 'Google', tone: 'bg-red-100 text-red-700' };
  }
  if (u.device_id) {
    return { label: 'Device UUID', tone: 'bg-blue-100 text-blue-700' };
  }
  return { label: 'Email/Password', tone: 'bg-slate-100 text-slate-700' };
}

const copyToClipboard = (label, value) => {
  if (!value) return;
  try {
    navigator.clipboard.writeText(String(value));
    toast.success(`${label} copied`);
  } catch {
    toast.error('Copy failed');
  }
};


export default function AdminUserDetails() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showTempPasswordModal, setShowTempPasswordModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showHardDeleteModal, setShowHardDeleteModal] = useState(false);
  const [hardDeleteEmail, setHardDeleteEmail] = useState('');
  const [tempPassword, setTempPassword] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [settingPassword, setSettingPassword] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    role: '',
  });
  const [relinkSeat, setRelinkSeat] = useState(null);
  const [relinkAthleteId, setRelinkAthleteId] = useState('');

  useEffect(() => {
    fetchUserDetails();
  }, [userId]);

  const fetchUserDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/admin/users/${userId}`);
      console.log('AdminUserDetails: API response', response.data);
      
      if (!response.data || !response.data.user) {
        console.error('AdminUserDetails: Invalid response - no user data');
        toast.error('User data not found');
        setUserData(null);
        return;
      }
      
      setUserData(response.data);
      const user = response.data.user;
      setEditForm({
        name: user.name || '',
        email: user.email || '',
        role: user.role || 'fan',
      });
    } catch (error) {
      console.error('AdminUserDetails: Failed to fetch user:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to load user: ${errorMessage}`);
      setUserData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUser = async () => {
    try {
      await api.patch(`/admin/users/${userId}`, editForm);
      toast.success('User updated successfully');
      setShowEditModal(false);
      fetchUserDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleSetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setSettingPassword(true);
    try {
      await api.patch(`/admin/users/${userId}/set-password`, { password: newPassword });
      toast.success('Password updated successfully');
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set password');
    } finally {
      setSettingPassword(false);
    }
  };

  const handleDeleteUser = async () => {
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success('User archived successfully');
      navigate('/admin/users');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to archive user');
    }
  };

  const handleRestoreUser = async () => {
    try {
      await api.post(`/admin/users/${userId}/restore`, {});
      toast.success('User restored successfully');
      fetchUserDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore user');
    }
  };

  const handleToggleTestUser = async () => {
    try {
      const newValue = !user?.is_test_user;
      await api.patch(`/admin/users/${userId}/test-user`, { is_test_user: newValue });
      toast.success(newValue ? 'Account marked as Test User' : 'Account changed to Customer');
      fetchUserDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user type');
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    try {
      await api.patch(`/admin/users/${userId}/status`, { status: newStatus });
      toast.success(`User status updated to ${newStatus}`);
      setShowStatusModal(false);
      fetchUserDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleHardDelete = async () => {
    if (hardDeleteEmail !== user?.email) {
      toast.error('Please type the email correctly to confirm deletion');
      return;
    }
    try {
      const response = await api.delete(`/admin/users/${userId}/hard-delete`);
      toast.success(`Test account permanently deleted. Teams: ${response.data.deleted_data?.teams_deleted || 0}, Games: ${response.data.deleted_data?.games_deleted || 0}`);
      navigate('/admin/users');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleGenerateTempPassword = async () => {
    try {
      const response = await api.post(`/admin/users/${userId}/temp-password`, {});
      setTempPassword(response.data.temp_password);
      setShowTempPasswordModal(true);
      toast.success('Temporary password generated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate temporary password');
    }
  };

  const handleImpersonate = async () => {
    try {
      await api.post(`/admin/impersonate/${userId}`, {});
      toast.success(`Now impersonating ${userData?.user?.name || userData?.user?.email || 'user'}`);
      window.location.href = '/dashboard';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to impersonate user');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'coach': return 'bg-blue-100 text-blue-700';
      case 'staff': return 'bg-teal-100 text-teal-700';
      case 'player': return 'bg-orange-100 text-orange-700';
      case 'fan': return 'bg-purple-100 text-purple-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getEngagementLevel = (metrics) => {
    if (!metrics) return { label: 'Low', color: 'text-slate-600 bg-slate-50' };
    const score = (metrics.logins_last_30_days || 0) + 
                  (metrics.games_created || 0) * 2 + 
                  (metrics.teams_owned || 0) * 3;
    if (score >= 20) return { label: 'Power User', color: 'text-green-600 bg-green-50' };
    if (score >= 10) return { label: 'Active', color: 'text-blue-600 bg-blue-50' };
    if (score >= 3) return { label: 'Regular', color: 'text-amber-600 bg-amber-50' };
    return { label: 'Low', color: 'text-slate-600 bg-slate-50' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-600">User not found</p>
        <Button onClick={() => navigate('/admin/users')} className="mt-4">
          Back to Users
        </Button>
      </div>
    );
  }

  const { user, teams, owned_teams, business_metrics, fan_seats } = userData;
  
  // Guard against missing user object
  if (!user) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-600">User data not available</p>
        <Button onClick={() => navigate('/admin/users')} className="mt-4">
          Back to Users
        </Button>
      </div>
    );
  }
  
  const engagement = getEngagementLevel(business_metrics || {});

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/admin/users')}
            className="p-2"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center space-x-3">
              <h1 className="text-2xl font-bold text-slate-900">{user.name || 'Unnamed User'}</h1>
              {user.role && (
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getRoleBadgeColor(user.role)}`}>
                  {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                </span>
              )}
              {user.role === 'admin' && (
                <span className="px-2 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-700">
                  Admin
                </span>
              )}
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${engagement.color}`}>
                {engagement.label}
              </span>
            </div>
            <p className="text-slate-600">{user.email}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {!user.deleted_at && (
            <>
              <Button
                variant="outline"
                onClick={handleImpersonate}
                className="text-purple-600 border-purple-200 hover:bg-purple-50"
              >
                <UserCog className="w-4 h-4 mr-2" />
                Impersonate
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowEditModal(true)}
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit
              </Button>
              <Button
                variant="outline"
                onClick={handleGenerateTempPassword}
                className="text-amber-600 border-amber-200 hover:bg-amber-50"
              >
                <Key className="w-4 h-4 mr-2" />
                Reset Password
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowDeleteModal(true)}
                className="text-red-600 border-red-200 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            </>
          )}
          {user.deleted_at && (
            <Button
              onClick={handleRestoreUser}
              className="bg-green-600 hover:bg-green-700"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Restore User
            </Button>
          )}
        </div>
      </div>

      {/* Status Banners */}
      {user.deleted_at && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-3">
          <AlertTriangle className="w-5 h-5 text-red-600" />
          <div>
            <p className="font-medium text-red-900">This user has been deleted</p>
            <p className="text-sm text-red-700">Deleted on {formatDate(user.deleted_at)}</p>
          </div>
        </div>
      )}

      {/* Athlete ID Section (for Players) */}
      {user.role === 'player' && (
        <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="admin-athlete-id-section">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Athlete ID</p>
              <p className="text-lg font-mono font-bold tracking-wider text-slate-900" data-testid="admin-athlete-id-value">
                {user.athlete_id || 'Not assigned'}
              </p>
            </div>
            {!user.athlete_id && (
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    await api.post('/admin/backfill-athlete-ids');
                    toast.success('Athlete ID generated');
                    fetchUserData();
                  } catch (e) {
                    toast.error('Failed to generate');
                  }
                }}
                data-testid="admin-generate-athlete-id"
              >
                Generate
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Fan Seats Section (for Fans) */}
      {user.role === 'fan' && (
        <div className="bg-white border border-slate-200 rounded-lg" data-testid="admin-fan-seats-section">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <h3 className="font-bold text-slate-900 text-sm uppercase tracking-wider">Athlete Seats ({fan_seats?.length || 0})</h3>
            <span className="text-xs font-medium text-teal-700 bg-teal-50 px-2 py-1 rounded" data-testid="fan-seat-count-badge">
              {fan_seats?.length || 0} seat{(fan_seats?.length || 0) !== 1 ? 's' : ''} used
            </span>
          </div>
          {!fan_seats || fan_seats.length === 0 ? (
            <div className="p-4 text-sm text-slate-500">No player seats created yet</div>
          ) : (
          <div className="divide-y divide-slate-100">
            {fan_seats.map((seat) => (
              <div key={seat.player_id} className="p-4 flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-900">{seat.name} <span className="text-slate-500">#{seat.jersey_number || '--'}</span></p>
                  <p className="text-sm text-slate-500">{seat.team_name} &middot; {seat.season}</p>
                  <p className="text-xs font-mono text-slate-600 mt-1">
                    Athlete ID: <strong>{seat.athlete_id || 'None'}</strong>
                    {seat.linked_user_id && <span className="ml-2 text-green-600">(Linked)</span>}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-blue-600 border-blue-200 hover:bg-blue-50"
                    onClick={() => { setRelinkSeat(seat); setRelinkAthleteId(''); }}
                    data-testid={`admin-relink-seat-${seat.player_id}`}
                  >
                    <Link2 className="w-3.5 h-3.5 mr-1" />
                    Override
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    onClick={async () => {
                      try {
                        await api.post(`/admin/fan-seats/${seat.player_id}/unlink-athlete`);
                        toast.success('Seat unlinked');
                        fetchUserDetails();
                      } catch (e) {
                        toast.error(e.response?.data?.detail || 'Failed to unlink');
                      }
                    }}
                    data-testid={`admin-unlink-seat-${seat.player_id}`}
                  >
                    Unlink
                  </Button>
                </div>
              </div>
            ))}
          </div>
          )}
        </div>
      )}

      {/* Business Metrics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <Activity className="w-5 h-5 text-blue-600" />
          </div>
          <p className="text-2xl font-bold text-slate-900">{business_metrics?.total_logins || 0}</p>
          <p className="text-xs text-slate-500 uppercase tracking-wider">Total Logins</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <TrendingUp className="w-5 h-5 text-green-600" />
          </div>
          <p className="text-2xl font-bold text-slate-900">{business_metrics?.logins_last_30_days || 0}</p>
          <p className="text-xs text-slate-500 uppercase tracking-wider">Last 30 Days</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <Zap className="w-5 h-5 text-amber-600" />
          </div>
          <p className="text-2xl font-bold text-slate-900">{business_metrics?.active_sessions || 0}</p>
          <p className="text-xs text-slate-500 uppercase tracking-wider">Active Sessions</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <Users className="w-5 h-5 text-purple-600" />
          </div>
          <p className="text-2xl font-bold text-slate-900">{business_metrics?.teams_owned || 0}</p>
          <p className="text-xs text-slate-500 uppercase tracking-wider">Teams Owned</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <Target className="w-5 h-5 text-emerald-600" />
          </div>
          <p className="text-2xl font-bold text-slate-900">{(business_metrics?.games_created || 0) + (business_metrics?.games_tracked || 0)}</p>
          <p className="text-xs text-slate-500 uppercase tracking-wider">Games Tracked</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <BarChart3 className="w-5 h-5 text-indigo-600" />
          </div>
          <p className="text-2xl font-bold text-slate-900">{business_metrics?.players_managed || business_metrics?.fan_players_created || 0}</p>
          <p className="text-xs text-slate-500 uppercase tracking-wider">Players</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Account Info Card */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
            <User className="w-5 h-5 mr-2 text-slate-600" />
            Account Information
          </h2>
          
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="text-sm font-medium text-slate-500">User ID</label>
              <p className="text-sm font-mono text-slate-700 bg-slate-50 px-2 py-1 rounded mt-1">{user.user_id}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-500">Email</label>
              <p className="text-slate-900 flex items-center mt-1">
                <Mail className="w-4 h-4 mr-2 text-slate-400" />
                {user.email}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-500">Account Created</label>
              <p className="text-slate-900 flex items-center mt-1">
                <Calendar className="w-4 h-4 mr-2 text-slate-400" />
                {formatDate(user.created_at)}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-500">Last Login</label>
              <p className="text-slate-900 flex items-center mt-1">
                <Clock className="w-4 h-4 mr-2 text-slate-400" />
                {formatDate(business_metrics?.last_login)}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-500">Account Age</label>
              <p className="text-slate-900 mt-1">{business_metrics?.account_age_days || 0} days</p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-500">Auth Provider</label>
              <p className="mt-1" data-testid="admin-user-auth-provider">
                {(() => {
                  const ap = deriveAuthProvider(user);
                  return (
                    <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${ap.tone}`}>
                      {ap.label}
                    </span>
                  );
                })()}
              </p>
            </div>
          </div>

          {/* Team Limit Override (Team Manager / Coach only) */}
          {(user.role === 'coach' || user.role === 'team_manager') && (
            <div className="mt-6 pt-6 border-t border-slate-200">
              <div className="p-4 border border-amber-200 bg-amber-50 rounded-lg" data-testid="team-limit-section">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-slate-800">Extended Team Limit</p>
                    <p className="text-xs text-slate-500 mt-0.5">Allow up to 5 teams instead of 3</p>
                  </div>
                  <button
                    onClick={async () => {
                      try {
                        const newVal = !user.team_limit_override;
                        await api.post(`/admin/users/${user.user_id}/team-limit-override`, { team_limit_override: newVal });
                        setUserData(prev => ({ ...prev, user: { ...prev.user, team_limit_override: newVal } }));
                      } catch (err) {
                        console.error('Team limit override failed', err);
                      }
                    }}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                      user.team_limit_override ? 'bg-amber-500' : 'bg-slate-300'
                    }`}
                    data-testid="team-limit-toggle"
                  >
                    <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform shadow-sm ${
                      user.team_limit_override ? 'translate-x-[18px]' : 'translate-x-[3px]'
                    }`} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Status Card */}
        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
            <Shield className="w-5 h-5 mr-2 text-slate-600" />
            Account Status
          </h2>
          
          <div className="space-y-3">
            {/* User Type */}
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <span className="text-sm text-slate-600">User Type</span>
              <span className={`px-2 py-1 rounded text-xs font-bold ${
                user.is_test_user 
                  ? 'bg-red-100 text-red-700' 
                  : 'bg-green-100 text-green-700'
              }`} data-testid="user-type-badge">
                {user.is_test_user ? 'TEST USER' : 'CUSTOMER'}
              </span>
            </div>

            {/* Customer Status */}
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <span className="text-sm text-slate-600">Status</span>
              <span className={`px-2 py-1 rounded text-xs font-bold ${
                user.deleted_at 
                  ? 'bg-red-100 text-red-700' 
                  : user.status === 'inactive' || user.is_suspended
                    ? 'bg-slate-200 text-slate-600'
                    : 'bg-green-100 text-green-700'
              }`} data-testid="status-badge">
                {user.deleted_at ? 'Deleted' : user.status === 'inactive' || user.is_suspended ? 'Inactive' : 'Active'}
              </span>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <span className="text-sm text-slate-600">Admin Access</span>
              <span className={`px-2 py-1 rounded text-xs font-medium ${
                user.role === 'admin' 
                  ? 'bg-purple-100 text-purple-700' 
                  : 'bg-slate-100 text-slate-700'
              }`}>
                {user.role === 'admin' ? 'Yes' : 'No'}
              </span>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <span className="text-sm text-slate-600">Email Verified</span>
              <span className={`px-2 py-1 rounded text-xs font-medium ${
                user.email_verified 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-amber-100 text-amber-700'
              }`}>
                {user.email_verified ? 'Yes' : 'No'}
              </span>
            </div>

            {user.must_change_password && (
              <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                <span className="text-sm text-amber-700">Password Reset Required</span>
                <AlertTriangle className="w-4 h-4 text-amber-600" />
              </div>
            )}

            {/* Action Buttons */}
            {!user.deleted_at && user.role !== 'admin' && (
              <div className="pt-3 space-y-2">
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={handleToggleTestUser}
                  data-testid="toggle-test-user-btn"
                >
                  <FlaskConical className={`w-4 h-4 mr-2 ${user.is_test_user ? 'text-red-600' : 'text-slate-600'}`} />
                  {user.is_test_user ? 'Change to Customer Account' : 'Mark as Test Account'}
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => setShowStatusModal(true)}
                  data-testid="update-status-btn"
                >
                  <UserCheck className={`w-4 h-4 mr-2 ${user.status === 'inactive' || user.is_suspended ? 'text-slate-400' : 'text-green-600'}`} />
                  Update Customer Status
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Identity & Auth Anchors — diagnoses which auth path created /
          last touched this account. All fields render even when null so
          the panel always tells you "what's NOT set" too. */}
      <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="admin-identity-anchors">
        <h2 className="text-lg font-semibold text-slate-900 mb-1 flex items-center">
          <Fingerprint className="w-5 h-5 mr-2 text-slate-600" />
          Identity &amp; Auth Anchors
        </h2>
        <p className="text-xs text-slate-500 mb-4">
          Raw identity fields used by <code className="font-mono bg-slate-50 px-1 rounded">/auth/device</code>,{' '}
          <code className="font-mono bg-slate-50 px-1 rounded">/auth/app-store</code>, and{' '}
          <code className="font-mono bg-slate-50 px-1 rounded">/auth/apple</code>. Cross-device sync keys off the
          App Store composite (<code className="font-mono">apple_app_apple_id</code> +{' '}
          <code className="font-mono">apple_original_purchase_date</code>).
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { label: 'Account ID', key: 'account_id', value: user.account_id },
            { label: 'Device ID', key: 'device_id', value: user.device_id, mono: true },
            { label: 'Device Platform', key: 'device_platform', value: user.device_platform },
            { label: 'App Version', key: 'app_version', value: user.app_version },
            { label: 'Apple App Apple ID', key: 'apple_app_apple_id', value: user.apple_app_apple_id, mono: true },
            { label: 'Apple Original Purchase Date', key: 'apple_original_purchase_date', value: user.apple_original_purchase_date, mono: true },
            { label: 'Apple App Transaction ID', key: 'apple_app_transaction_id', value: user.apple_app_transaction_id, mono: true },
            { label: 'Apple Environment', key: 'apple_environment', value: user.apple_environment },
            { label: 'Apple Subject ID (SIWA)', key: 'apple_subject_id', value: user.apple_subject_id, mono: true },
            { label: 'Google Subject ID', key: 'google_subject_id', value: user.google_subject_id || user.google_id, mono: true },
            { label: 'Superseded By', key: 'superseded_by', value: user.superseded_by, mono: true },
            { label: 'Contact Email', key: 'contact_email', value: user.contact_email },
          ].map(({ label, key, value, mono }) => {
            const isSet = value !== null && value !== undefined && value !== '';
            return (
              <div
                key={key}
                className={`p-3 rounded-lg border ${
                  isSet ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50'
                }`}
                data-testid={`admin-anchor-${key}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</span>
                  {isSet && (
                    <button
                      type="button"
                      onClick={() => copyToClipboard(label, value)}
                      className="text-slate-400 hover:text-slate-600 p-1 -m-1"
                      data-testid={`admin-anchor-copy-${key}`}
                      aria-label={`Copy ${label}`}
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                <p
                  className={`text-sm break-all ${
                    isSet ? 'text-slate-900' : 'text-slate-400 italic'
                  } ${mono ? 'font-mono' : ''}`}
                  data-testid={`admin-anchor-value-${key}`}
                >
                  {isSet ? String(value) : 'not set'}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Teams Section */}
      {owned_teams && owned_teams.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
            <Users className="w-5 h-5 mr-2 text-slate-600" />
            Teams Owned ({owned_teams.length})
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {owned_teams.map((team, index) => (
              <div
                key={team?.team_id || `owned-${index}`}
                className="p-4 border border-slate-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors"
              >
                <h3 className="font-medium text-slate-900">{team?.name || 'Unnamed Team'}</h3>
                <p className="text-sm text-slate-500">{team?.level || 'No level set'}</p>
                <div className="flex items-center justify-between mt-2">
                  <p className="text-xs text-slate-400">Code: {team?.join_code || 'N/A'}</p>
                  <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                    {team?.season_name || 'Default Season'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Member Teams Section */}
      {teams && teams.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
            <Trophy className="w-5 h-5 mr-2 text-slate-600" />
            Team Memberships ({teams.length})
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {teams.map((team, index) => (
              <div
                key={team?.team_id || `member-${index}`}
                className="p-4 border border-slate-200 rounded-lg"
              >
                <h3 className="font-medium text-slate-900">{team?.name || 'Unnamed Team'}</h3>
                <p className="text-sm text-slate-500">{team?.level || 'No level set'}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="role">Role</Label>
              <Select
                value={editForm.role}
                onValueChange={(value) => setEditForm({ ...editForm, role: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="coach">Coach</SelectItem>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="player">Player</SelectItem>
                  <SelectItem value="fan">Fan</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Direct Password Set */}
            <div className="border-t pt-4 mt-4">
              <Label className="text-sm font-medium">Set New Password</Label>
              <p className="text-xs text-slate-500 mb-2">Directly set a new password for this user (no old password required)</p>
              <div className="flex gap-2">
                <Input
                  type="password"
                  placeholder="New password (min 6 chars)"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
                <Button onClick={handleSetPassword} disabled={settingPassword || !newPassword}>
                  {settingPassword ? 'Setting...' : 'Set'}
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateUser}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600">
              Are you sure you want to delete <strong>{user.name || user.email}</strong>?
              This action can be undone by restoring the user.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleDeleteUser}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Temp Password Modal */}
      <Dialog open={showTempPasswordModal} onOpenChange={setShowTempPasswordModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Temporary Password Generated</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600 mb-4">
              Share this temporary password with the user. They will be required to change it on their next login.
            </p>
            <div className="bg-slate-100 p-4 rounded-lg">
              <p className="text-sm text-slate-500 mb-1">Temporary Password:</p>
              <p className="text-2xl font-mono font-bold text-slate-900 select-all">
                {tempPassword}
              </p>
            </div>
            <p className="text-sm text-amber-600 mt-4">
              This password will expire in 24 hours.
            </p>
          </div>
          <DialogFooter>
            <Button onClick={() => {
              navigator.clipboard.writeText(tempPassword);
              toast.success('Password copied to clipboard');
            }} variant="outline">
              Copy to Clipboard
            </Button>
            <Button onClick={() => setShowTempPasswordModal(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update Status Modal */}
      <Dialog open={showStatusModal} onOpenChange={setShowStatusModal}>
        <DialogContent data-testid="status-modal">
          <DialogHeader>
            <DialogTitle className="font-bold uppercase">Update Customer Status</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600 mb-4">
              Set status for <strong>{user?.name}</strong> ({user?.email})
            </p>
            <div className="space-y-3 mb-6">
              <Button
                variant={user?.status !== 'inactive' && !user?.is_suspended ? 'default' : 'outline'}
                className={`w-full justify-start ${user?.status !== 'inactive' && !user?.is_suspended ? 'bg-green-600 hover:bg-green-700' : ''}`}
                onClick={() => handleUpdateStatus('active')}
              >
                <UserCheck className="w-4 h-4 mr-2" />
                Active
                <span className="ml-auto text-xs opacity-70">User can access the app</span>
              </Button>
              <Button
                variant={user?.status === 'inactive' || user?.is_suspended ? 'default' : 'outline'}
                className={`w-full justify-start ${user?.status === 'inactive' || user?.is_suspended ? 'bg-slate-600 hover:bg-slate-700' : ''}`}
                onClick={() => handleUpdateStatus('inactive')}
              >
                <AlertTriangle className="w-4 h-4 mr-2" />
                Inactive
                <span className="ml-auto text-xs opacity-70">User is blocked from access</span>
              </Button>
            </div>
            <Button variant="outline" className="w-full" onClick={() => setShowStatusModal(false)}>
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Hard Delete Modal */}
      <Dialog open={showHardDeleteModal} onOpenChange={() => { setShowHardDeleteModal(false); setHardDeleteEmail(''); }}>
        <DialogContent data-testid="hard-delete-modal">
          <DialogHeader>
            <DialogTitle className="font-bold uppercase text-red-600 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Permanently Delete Test Account
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 space-y-2 text-sm text-red-800">
              <p className="font-semibold">You are permanently deleting this test account. This cannot be undone.</p>
              <ul className="list-disc list-inside space-y-1 text-red-700">
                <li>All teams associated with this account will be deleted.</li>
                <li>All games for those teams will be deleted.</li>
                <li>Claimed player accounts will remain active until permanently deleted.</li>
              </ul>
            </div>
            <p className="text-slate-600 mb-4">
              To confirm, type the user's email: <strong className="font-mono">{user?.email}</strong>
            </p>
            <Input
              value={hardDeleteEmail}
              onChange={(e) => setHardDeleteEmail(e.target.value)}
              placeholder="Type email to confirm"
              className="mb-4"
              data-testid="hard-delete-email-input"
            />
            <div className="flex space-x-3">
              <Button variant="outline" className="flex-1" onClick={() => { setShowHardDeleteModal(false); setHardDeleteEmail(''); }}>
                Cancel
              </Button>
              <Button 
                className="flex-1 bg-red-600 hover:bg-red-700" 
                onClick={handleHardDelete}
                disabled={hardDeleteEmail !== user?.email}
                data-testid="confirm-hard-delete-btn"
              >
                Permanently Delete
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Relink Athlete ID Modal */}
      <Dialog open={!!relinkSeat} onOpenChange={() => { setRelinkSeat(null); setRelinkAthleteId(''); }}>
        <DialogContent data-testid="relink-athlete-modal">
          <DialogHeader>
            <DialogTitle className="font-bold uppercase flex items-center gap-2">
              <Link2 className="w-5 h-5 text-blue-600" />
              Override Athlete ID
            </DialogTitle>
          </DialogHeader>
          {relinkSeat && (
            <div className="py-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-blue-800">
                  Force-override the Athlete ID for <strong>{relinkSeat.name}</strong>.
                  This will replace the current ID and link the seat to the target player account if one exists.
                </p>
              </div>
              <div className="space-y-3 mb-4">
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <span className="text-sm text-slate-600">Current Athlete ID</span>
                  <span className="font-mono font-bold text-slate-900" data-testid="relink-current-aid">{relinkSeat.athlete_id || 'None'}</span>
                </div>
                <div>
                  <Label className="text-sm font-bold uppercase tracking-wider text-slate-600 mb-2 block">New Athlete ID</Label>
                  <Input
                    value={relinkAthleteId}
                    onChange={(e) => setRelinkAthleteId(e.target.value.toUpperCase())}
                    placeholder="e.g. KT689G1TQ5"
                    className="font-mono h-12 text-lg tracking-wider"
                    data-testid="relink-athlete-id-input"
                  />
                </div>
              </div>
              <div className="flex space-x-3">
                <Button variant="outline" className="flex-1" onClick={() => { setRelinkSeat(null); setRelinkAthleteId(''); }}>
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  disabled={!relinkAthleteId.trim() || relinkAthleteId.trim() === relinkSeat.athlete_id}
                  onClick={async () => {
                    try {
                      await api.post(`/admin/fan-seats/${relinkSeat.player_id}/relink-athlete`, {
                        athlete_id: relinkAthleteId.trim()
                      });
                      toast.success(`Athlete ID overridden to ${relinkAthleteId.trim()}`);
                      setRelinkSeat(null);
                      setRelinkAthleteId('');
                      fetchUserDetails();
                    } catch (e) {
                      toast.error(e.response?.data?.detail || 'Failed to override Athlete ID');
                    }
                  }}
                  data-testid="confirm-relink-btn"
                >
                  Override Athlete ID
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
